/* eslint-disable @typescript-eslint/no-explicit-any */

import {
    HttpErrorResponse,
    HttpEvent,
    HttpHandler,
    HttpInterceptor,
    HttpRequest
} from "@angular/common/http";
import { Injectable } from "@angular/core";
import { MatLegacyDialog as MatDialog } from "@angular/material/legacy-dialog";
import { Router } from "@angular/router";
import { BehaviorSubject, Observable, throwError } from "rxjs";
import { catchError, filter, switchMap, take } from "rxjs/operators";
import { AuthService } from "./auth.service";

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
    isRefreshingToken = false;
    tokenSubject: BehaviorSubject<string> = new BehaviorSubject<string>(null);

    constructor(
        private authService: AuthService,
        private router: Router,
        private dialog: MatDialog
    ) {}

    intercept(
        request: HttpRequest<any>,
        next: HttpHandler
    ): Observable<HttpEvent<any>> {
        if (this.authService.accessToken) {
            return next.handle(request).pipe(
                catchError((err: unknown) => {
                    if (
                        err instanceof HttpErrorResponse &&
                        err.status === 401
                    ) {
                        // Hitting 401 on /authorize/refresh means our refresh token is expired,
                        // so go ahead and logout immediately
                        return request.url.includes("/authorize/refresh") ?
                                this.logout(
                                    err,
                                    "Browser session could not be refreshed."
                                )
                            :   this.handle401(request, next);
                    } else if (
                        err instanceof HttpErrorResponse &&
                        err.status === 500
                    ) {
                        return this.handle500();
                    } else {
                        return throwError(err);
                    }
                })
            );
        }

        return next.handle(request);
    }

    private addToken(
        request: HttpRequest<any>,
        token: string
    ): HttpRequest<any> {
        return request.clone({
            setHeaders: {
                Authorization: `bearer ${token}` // eslint-disable-line @typescript-eslint/naming-convention
            }
        });
    }

    private logout(err: any, errorMessage: string) {
        this.dialog.closeAll();
        this.authService.logout();
        this.router.navigate(["/login"], {
            queryParams: {
                msg: errorMessage
            }
        });

        return throwError(err);
    }

    private handle500(): Observable<HttpEvent<any>> {
        this.router.navigateByUrl("/app/unexpected-error");
        return throwError(new ErrorEvent("500"));
    }

    private handle401(
        request: HttpRequest<any>,
        next: HttpHandler
    ): Observable<HttpEvent<any>> {
        if (this.isRefreshingToken) {
            return this.tokenSubject.pipe(
                filter((token) => token !== null),
                take(1),
                switchMap((token) => next.handle(this.addToken(request, token)))
            );
        }

        this.isRefreshingToken = true;
        this.tokenSubject.next(null);

        return this.authService.refresh(request.url).pipe(
            switchMap((res) => {
                this.isRefreshingToken = false;
                this.tokenSubject.next(res.accessToken);
                return next.handle(this.addToken(request, res.accessToken));
            }),
            catchError((err: unknown) =>
                // Scenario: Truly unauthorized request call?
                // I could force this by always returning an Unauthorized() results
                // from an api endpoint, but otherwise wouldn't expect to see this much.
                this.logout(
                    err,
                    `An unexpected error occurred and you have been logged out.`
                )
            )
        );
    }
}
